import * as THREE from 'three'
import {
    acceleratedRaycast,
    computeBoundsTree,
    disposeBoundsTree,
    CENTER,
} from 'three-mesh-bvh'

const RUNTIME_KEY = '__AO3D_PERF_RUNTIME__'
const MODEL_REGISTRY_KEY = '__AO3D_MODEL_METRICS__'
const BVH_SCHEDULED_KEY = '__ao3dBvhScheduled'
const BVH_READY_KEY = '__ao3dBvhReady'
const BVH_ERROR_KEY = '__ao3dBvhError'
const BVH_BUILD_KEY = '__ao3dBvhBuild'
const BVH_CANCEL_KEY = '__ao3dBvhCancel'
const PATCHED_KEY = '__ao3dBvhPatched'

const getGlobalObject = () => {
    if (typeof window !== 'undefined') return window
    return null
}

export const getScenePerformanceRuntime = () => {
    const root = getGlobalObject()

    if (!root) {
        return {
            enabled: false,
            latestSnapshot: null,
            raycast: {
                calls: 0,
                totalMs: 0,
                maxMs: 0,
                lastMs: 0,
            },
        }
    }

    if (!root[RUNTIME_KEY]) {
        root[RUNTIME_KEY] = {
            enabled: false,
            latestSnapshot: null,
            raycast: {
                calls: 0,
                totalMs: 0,
                maxMs: 0,
                lastMs: 0,
            },
            loggingTimer: null,
        }
    }

    return root[RUNTIME_KEY]
}

export const getSceneModelPerformanceRegistry = () => {
    const root = getGlobalObject()

    if (!root) return new Map()

    if (!root[MODEL_REGISTRY_KEY]) {
        root[MODEL_REGISTRY_KEY] = new Map()
    }

    return root[MODEL_REGISTRY_KEY]
}

export const installSceneBvhAcceleration = () => {
    if (THREE.Mesh.prototype[PATCHED_KEY]) return

    if (typeof THREE.BufferGeometry.prototype.computeBoundsTree !== 'function') {
        THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree
    }

    if (typeof THREE.BufferGeometry.prototype.disposeBoundsTree !== 'function') {
        THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree
    }

    THREE.Mesh.prototype.raycast = acceleratedRaycast
    THREE.Mesh.prototype[PATCHED_KEY] = true
}

const scheduleIdleTask = (callback, timeout = 2400) => {
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
        const id = window.requestIdleCallback(callback, { timeout })
        return () => window.cancelIdleCallback?.(id)
    }

    const id = setTimeout(callback, 32)
    return () => clearTimeout(id)
}

const getIndexCount = (geometry) => {
    if (!geometry) return 0
    if (geometry.index) return Number(geometry.index.count || 0)
    return Number(geometry.getAttribute?.('position')?.count || 0)
}

export const getGeometryTriangleCount = (geometry) => {
    return Math.floor(getIndexCount(geometry) / 3)
}

export const getGeometryVertexCount = (geometry) => {
    return Number(geometry?.getAttribute?.('position')?.count || 0)
}

export const estimateGeometryBytes = (geometry) => {
    if (!geometry) return 0

    let bytes = 0

    Object.values(geometry.attributes || {}).forEach((attribute) => {
        const array = attribute?.isInterleavedBufferAttribute
            ? attribute.data?.array
            : attribute?.array

        if (array?.byteLength) bytes += Number(array.byteLength)
    })

    if (geometry.index?.array?.byteLength) {
        bytes += Number(geometry.index.array.byteLength)
    }

    Object.values(geometry.morphAttributes || {}).forEach((attributes) => {
        ;(Array.isArray(attributes) ? attributes : []).forEach((attribute) => {
            const array = attribute?.isInterleavedBufferAttribute
                ? attribute.data?.array
                : attribute?.array

            if (array?.byteLength) bytes += Number(array.byteLength)
        })
    })

    return bytes
}

export const getGeometryAttributeSummary = (geometry) => {
    const summary = {}

    Object.entries(geometry?.attributes || {}).forEach(([name, attribute]) => {
        const array = attribute?.isInterleavedBufferAttribute
            ? attribute.data?.array
            : attribute?.array

        summary[name] = {
            count: Number(attribute?.count || 0),
            itemSize: Number(attribute?.itemSize || 0),
            bytes: Number(array?.byteLength || 0),
        }
    })

    return summary
}

export const registerSceneModelPerformance = (modelId, patch = {}) => {
    const id = String(modelId || '').trim()

    if (!id) return null

    const registry = getSceneModelPerformanceRegistry()
    const previous = registry.get(id) || {}
    const next = {
        ...previous,
        ...patch,
        modelId: id,
        updatedAt: Date.now(),
    }

    registry.set(id, next)

    return next
}

export const prepareGeometryForFastRaycast = (
    geometry,
    {
        modelId = '',
        defer = true,
        maxLeafTris = 32,
    } = {}
) => {
    if (!geometry?.isBufferGeometry) return () => {}

    installSceneBvhAcceleration()

    if (!geometry.boundingBox) geometry.computeBoundingBox()
    if (!geometry.boundingSphere) geometry.computeBoundingSphere()

    if (geometry.boundsTree || geometry.userData?.[BVH_READY_KEY]) {
        return () => {}
    }

    geometry.userData = geometry.userData || {}

    const clearScheduledState = () => {
        geometry.userData[BVH_SCHEDULED_KEY] = false
        geometry.userData[BVH_BUILD_KEY] = null
        geometry.userData[BVH_CANCEL_KEY] = null
    }

    const build = geometry.userData[BVH_BUILD_KEY] || (() => {
        clearScheduledState()

        if (!geometry?.attributes?.position) return
        if (geometry.userData?.[BVH_READY_KEY] || geometry.boundsTree) return

        const startedAt = performance.now()

        try {
            geometry.computeBoundsTree({
                strategy: CENTER,
                maxLeafTris: Math.max(8, Number(maxLeafTris) || 32),
                indirect: true,
                verbose: false,
            })

            const buildMs = performance.now() - startedAt

            geometry.userData[BVH_READY_KEY] = true
            geometry.userData[BVH_ERROR_KEY] = ''
            geometry.userData.__ao3dBvhBuildMs = buildMs

            if (modelId) {
                registerSceneModelPerformance(modelId, {
                    bvhReady: true,
                    bvhBuildMs: buildMs,
                })
            }
        } catch (error) {
            geometry.userData[BVH_ERROR_KEY] = error?.message || String(error)

            if (modelId) {
                registerSceneModelPerformance(modelId, {
                    bvhReady: false,
                    bvhError: error?.message || String(error),
                })
            }
        }
    })

    geometry.userData[BVH_BUILD_KEY] = build

    // Если геометрия уже ждала requestIdleCallback, но объект прямо сейчас
    // сделали твёрдым, отменяем ожидание и строим BVH немедленно.
    if (geometry.userData[BVH_SCHEDULED_KEY]) {
        if (!defer) {
            geometry.userData[BVH_CANCEL_KEY]?.()
            build()
        }

        return () => {}
    }

    geometry.userData[BVH_SCHEDULED_KEY] = true

    if (!defer || getGeometryTriangleCount(geometry) < 30000) {
        build()
        return () => {}
    }

    const cancelIdle = scheduleIdleTask(build)
    let cancelled = false

    const cancel = () => {
        if (cancelled) return
        cancelled = true
        cancelIdle?.()

        if (!geometry.boundsTree && !geometry.userData?.[BVH_READY_KEY]) {
            clearScheduledState()
        }
    }

    geometry.userData[BVH_CANCEL_KEY] = cancel

    return cancel
}

export const buildGeometryPerformanceMetrics = ({
    geometry,
    modelId,
    fileType,
    sourceBytes = 0,
    decodeMs = 0,
    mergeMs = 0,
    materialCount = 0,
    sourceMeshCount = 0,
    sourcePrimitiveCount = 0,
} = {}) => {
    const metrics = {
        modelId: String(modelId || ''),
        fileType: String(fileType || ''),
        sourceBytes: Number(sourceBytes || 0),
        decodedGeometryBytes: estimateGeometryBytes(geometry),
        vertices: getGeometryVertexCount(geometry),
        indices: Number(geometry?.index?.count || 0),
        triangles: getGeometryTriangleCount(geometry),
        indexed: !!geometry?.index,
        materialCount: Number(materialCount || 0),
        sourceMeshCount: Number(sourceMeshCount || 0),
        sourcePrimitiveCount: Number(sourcePrimitiveCount || 0),
        decodeMs: Number(decodeMs || 0),
        mergeMs: Number(mergeMs || 0),
        attributes: getGeometryAttributeSummary(geometry),
        bvhReady: !!geometry?.boundsTree,
    }

    if (metrics.modelId) {
        registerSceneModelPerformance(metrics.modelId, metrics)
    }

    return metrics
}

export const formatBytesCompact = (value) => {
    const bytes = Math.max(0, Number(value) || 0)

    if (bytes >= 1024 ** 3) return `${(bytes / (1024 ** 3)).toFixed(2)} GB`
    if (bytes >= 1024 ** 2) return `${(bytes / (1024 ** 2)).toFixed(1)} MB`
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`

    return `${Math.round(bytes)} B`
}

export const formatNumberCompact = (value) => {
    const number = Math.max(0, Number(value) || 0)

    if (number >= 1_000_000) return `${(number / 1_000_000).toFixed(2)}M`
    if (number >= 1_000) return `${(number / 1_000).toFixed(1)}K`

    return String(Math.round(number))
}

installSceneBvhAcceleration()
